import { addDays, NO_START, diffDay, dateToTimestamp } from '@ha/date';

import {
  NoStartDateMonthLimit,
  DateFlexibility,
  ShortRangeRentThreshold,
  MULTIUSE_START,
} from '../constants';

import { FilterBuilder } from '../Types';

const isShortRangeRent = (startDate?: string, endDate?: string): boolean =>
  Boolean(
    startDate &&
      endDate &&
      diffDay(endDate, startDate) <= ShortRangeRentThreshold,
  );

const getCurrentDateUtc = (): string => new Date().toUTCString();

const normalizeStartDate = (): string => {
  const startDateLimit = new Date();

  startDateLimit.setMonth(startDateLimit.getMonth() + NoStartDateMonthLimit);
  startDateLimit.setDate(0);
  startDateLimit.setHours(0, 0, 0);

  return startDateLimit.toDateString();
};

const gapMinimizeFilter = (startDate?: string, endDate?: string): string => {
  if (!startDate) {
    return '';
  }
  const minStartDateLimit = new Date(startDate);

  minStartDateLimit.setDate(1);

  const hasBothDatesAndIsShort = isShortRangeRent(startDate, endDate);
  const hasOnlyMoveInDateAndIsShort =
    !endDate && isShortRangeRent(getCurrentDateUtc(), startDate);

  if (hasBothDatesAndIsShort || hasOnlyMoveInDateAndIsShort) {
    minStartDateLimit.setMonth(minStartDateLimit.getMonth() - 1);
    minStartDateLimit.setDate(0);
  } else {
    minStartDateLimit.setMonth(minStartDateLimit.getMonth() - 3);
  }

  const minStartDateLimitTS = dateToTimestamp(minStartDateLimit);
  const multiuseStartDateTS = dateToTimestamp(new Date(MULTIUSE_START));
  const query = `dateFromTS>=${minStartDateLimitTS} OR dateFromTS=${multiuseStartDateTS}`;

  // include listings which doesn't have a start date only if user searches with a start date in near future
  if (minStartDateLimit < new Date()) {
    const noStartDateTS = dateToTimestamp(new Date(NO_START));

    return `(${query} OR dateFromTS=${noStartDateTS})`;
  }

  return `(${query})`;
};

const dateFromFilter = (startDate?: string, endDate?: string): string => {
  let start;

  if (startDate && endDate) {
    start = isShortRangeRent(startDate, endDate)
      ? (startDate as string)
      : addDays(startDate, DateFlexibility);
  } else if (startDate) {
    start = isShortRangeRent(getCurrentDateUtc(), startDate)
      ? (startDate as string)
      : addDays(startDate, DateFlexibility);
  } else {
    start = normalizeStartDate();
  }

  const startDateTS = dateToTimestamp(new Date(start));
  const noStartDateTS = dateToTimestamp(new Date(NO_START));
  const multiuseStartDateTS = dateToTimestamp(new Date(MULTIUSE_START));

  return `(dateFromTS<=${startDateTS} OR dateFromTS=${noStartDateTS} OR dateFromTS=${multiuseStartDateTS})`;
};

const dateFromTSBuilder: FilterBuilder = (
  { startDate, endDate, advertiserId },
  context,
) => {
  if ((!startDate && advertiserId) || context?.withNewDateFilter) {
    return '';
  }

  const dateFrom = dateFromFilter(startDate, endDate);
  const gapMinimize = gapMinimizeFilter(startDate, endDate);

  return [dateFrom, gapMinimize].filter(Boolean).join(' AND ');
};

export default dateFromTSBuilder;
